import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { X, ChevronLeft, Save, Play, Loader2, Database, Server, Smartphone, FileJson, Globe, AlertCircle, CheckCircle, Cloud, RefreshCw, Eye, EyeOff, UploadCloud, FileSpreadsheet, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ConnectorTile } from './ConnectorTile';
import { useAuth } from '@/context/useAuth';
import { apiGet, apiPost, apiPut, apiFetch } from '@/api';

// --- Types ---
interface Gateway {
    id: string;
    name: string;
    description?: string;
    workspaceId: string;
    isOnline: boolean;
    lastConnectedAt?: string;
    clientVersion?: string;
    createdAt: string;
}

// --- Configuration ---
const DATA_SOURCE_TYPES = [
    { value: '0', label: 'PostgreSQL', icon: Database, category: 'Relational' },
    { value: '1', label: 'MySQL', icon: Database, category: 'Relational' },
    { value: '2', label: 'SQL Server', icon: Server, category: 'Relational' },
    { value: '3', label: 'Oracle', icon: Database, category: 'Relational' },
    { value: '6', label: 'Redshift', icon: Globe, category: 'Cloud' },
    { value: '5', label: 'BigQuery', icon: Cloud, category: 'Cloud' },
    { value: '7', label: 'Azure SQL', icon: Globe, category: 'Cloud' },
    { value: '4', label: 'Snowflake', icon: Globe, category: 'Cloud' },
    { value: '13', label: 'DuckDB', icon: Database, category: 'Relational' },
    { value: 'file_upload', label: 'Local File', icon: UploadCloud, category: 'File' },
    { value: '15', label: 'MCP Server', icon: Smartphone, category: 'API' },
    { value: '16', label: 'Azure Blob Storage', icon: Globe, category: 'Cloud' },
    { value: '17', label: 'Salesforce', icon: Cloud, category: 'Cloud' },
];

const FIELDS_CONFIG: Record<string, string[]> = {
    '0': ['host', 'port', 'database', 'username', 'password'],
    '1': ['host', 'port', 'database', 'username', 'password'],
    '2': ['host', 'port', 'database', 'username', 'password'],
    '3': ['host', 'port', 'database', 'username', 'password'],
    '6': ['host', 'port', 'database', 'username', 'password'],
    '5': ['database'],
    '7': ['host', 'database', 'username', 'password'],
    '4': ['account', 'warehouse', 'database', 'username', 'password'],
    '13': ['filePath'],
    'file_upload': ['filePath'],
    '15': ['command'],
    '16': ['connectionString'],
    '17': ['environmentUrl', 'apiVersion'] // Base fields, others dynamic based on Auth Type
};


const formSchema = z.object({
    name: z.string().min(2, 'Name is required'),
    gatewayId: z.string().nullish(),
    host: z.string().nullish(),
    port: z.string().nullish(),
    database: z.string().nullish(),
    username: z.string().nullish(),
    password: z.string().nullish(),
    account: z.string().nullish(),
    warehouse: z.string().nullish(),
    filePath: z.string().nullish(),
    command: z.string().nullish(),
    connectionString: z.string().nullish(),
    clientId: z.string().nullish(),
    clientSecret: z.string().nullish(),
    securityToken: z.string().nullish(),
    environmentUrl: z.string().nullish(),
    apiVersion: z.string().nullish(),
    source: z.string().nullish(),
});

type FormValues = z.infer<typeof formSchema>;

interface DataSourceWizardProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    dataSourceId?: string | null;
}

export const DataSourceWizard = ({ isOpen, onClose, onSuccess, dataSourceId }: DataSourceWizardProps) => {
    const [step, setStep] = useState<'GALLERY' | 'CONFIG'>('GALLERY');
    const [selectedType, setSelectedType] = useState<string | null>(null);
    const [gateways, setGateways] = useState<Gateway[]>([]);
    const [isTesting, setIsTesting] = useState(false);
    const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

    // --- File Upload State ---
    const [dragActive, setDragActive] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [existingFileName, setExistingFileName] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const activeFields = (selectedType ? FIELDS_CONFIG[selectedType] : null) ?? [];
    const isFileSource = selectedType === 'file_upload';
    const [sfAuthMode, setSfAuthMode] = useState<'oauth' | 'legacy'>('oauth');
    const [showPassword, setShowPassword] = useState(false);

    // Auth
    const { user, token } = useAuth(); // Use useAuth hook

    // Discovery
    const [discoveredDbs, setDiscoveredDbs] = useState<string[]>([]);
    const [isDiscovering, setIsDiscovering] = useState(false);
    const [discoverError, setDiscoverError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // Form
    const { register, handleSubmit, reset, getValues, setValue, watch } = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            apiVersion: '52.0'
        }
    });

    useEffect(() => {
        if (isOpen) {
            setTestResult(null);
            setSfAuthMode('oauth');
            // Always clear file state on open to avoid stale data
            setSelectedFile(null);
            setExistingFileName(null);
            setUploadProgress(0);
            fetchGateways();

            if (dataSourceId) {
                loadDataSource(dataSourceId);
            } else {
                setStep('GALLERY');
                setSelectedType(null);
                reset({ apiVersion: '52.0' });
            }
        }
    }, [isOpen, dataSourceId, token, setValue]);

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
        else if (e.type === "dragleave") setDragActive(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileSelection(e.dataTransfer.files[0]);
        }
    };

    const handleFileSelection = (file: File) => {
        setSelectedFile(file);
        // Default the connection name to the file name if it's empty
        if (!watch('name')) {
            const baseName = file.name.split('.').slice(0, -1).join('.') || file.name;
            setValue('name', baseName);
        }
    };

    const handleFileUpload = async () => {
        if (!selectedFile) return;
        
        setIsUploading(true);
        setUploadProgress(10); // Start progress

        try {
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('name', watch('name') || selectedFile.name);
            // If editing an existing datasource, pass its ID so the backend updates it in-place
            if (dataSourceId) formData.append('existing_id', dataSourceId);

            // Simulate progress while uploading
            const progressInterval = setInterval(() => {
                setUploadProgress((prev) => (prev < 90 ? prev + 10 : prev));
            }, 500);

            // Provide token explicitly if the wrapper asks for it, and it will auto-assign headers for FormData
            const res = await apiFetch('/flows/ops/upload-datasource', token, {
                method: 'POST',
                body: formData
            });

            clearInterval(progressInterval);
            setUploadProgress(100);

            // Immediately mark success!
            setTimeout(() => {
                onSuccess();
                onClose();
            }, 600);

        } catch (e: any) {
            console.error('File Upload Error:', e);
            setTestResult({ success: false, message: e.message || 'File upload failed' });
            setUploadProgress(0);
        } finally {
            setIsUploading(false);
        }
    };

    const loadDataSource = async (id: string) => {
        setIsLoading(true);
        try {
            const data = await apiGet(`/datasources/${id}`, token);
            if (data) {
                // File types (CSV=9, Excel=10, JSON=11, Parquet=12) all use the unified file_upload UI
                const FILE_TYPE_VALUES = [9, 10, 11, 12];
                const rawType = data.type;
                const resolvedType = FILE_TYPE_VALUES.includes(Number(rawType)) ? 'file_upload' : rawType?.toString();
                setSelectedType(resolvedType);
                setStep('CONFIG');

                // Merge configurationJson into form values
                const config = data.configurationJson || {};
                // Show existing stored file name in the dropzone.
                // Fall back to extracting the filename from the S3 connection string.
                const storedFileName =
                    config.fileName ||
                    (data.connectionString ? data.connectionString.split('/').pop() : null);
                if (storedFileName) setExistingFileName(storedFileName);
                const formValues = {
                    name: data.name,

                    connectionString: data.connectionString,
                    ...config,
                    // Map legacy keys (Backend schema uses 'user', Frontend uses 'username')
                    username: config.username || config.user,
                    // Map SQL Server specific 'server' to 'host' if needed
                    host: config.host || config.server,
                    // Ensure Gateway ID is loaded from top-level OR config
                    gatewayId: data.gatewayId || config.gatewayId || "none"
                };

                // Sanitize Port if corrupted (e.g. "None" string from Python serialization issues)
                if (formValues.port === 'None' || formValues.port === null || formValues.port === undefined) {
                    formValues.port = ''; // Reset to empty to allow default or user input
                } else {
                    formValues.port = formValues.port.toString();
                }

                // Mask sensitive fields to prevent re-encrypting encrypted values
                const sensitiveFields = ['password', 'clientSecret', 'accessKey', 'secretKey', 'securityToken'];
                sensitiveFields.forEach(field => {
                    if (formValues[field]) {
                        formValues[field] = '********';
                    }
                });

                // Determine Salesforce Auth Mode if applicable
                if (data.type === 17 && formValues.grantType === 'password') {
                    setSfAuthMode('legacy');
                }

                reset(formValues);
            }
        } catch (e) {
            console.error("Failed to load datasource", e);
            setTestResult({ success: false, message: "Failed to load connection details. Please try again." });
            onClose();
        } finally {
            setIsLoading(false);
        }
    };

    const fetchGateways = async () => {
        if (!token) return;
        try {
            const data = await apiGet('/gateways', token);
            setGateways(data || []);
        } catch (e) {
            console.warn("Failed to fetch gateways", e);
        }
    };

    const handleSelectType = (type: string) => {
        setSelectedType(type);
        setStep('CONFIG');
        setDiscoveredDbs([]); // Reset functionality
    };

    const handleDiscoverDatabases = async () => {
        const values = getValues();
        if (!values.host || !values.username || !values.password) {
            setDiscoverError("Please enter Host, Username, and Password first.");
            return;
        }
        setDiscoverError(null);

        setIsDiscovering(true);
        try {
            const typeLabel = DATA_SOURCE_TYPES.find(t => t.value === selectedType)?.label || '';
            // Map label to provider string if needed, or backend handles it (it handles lower case match)

            const payload = {
                provider: typeLabel,
                host: values.host,
                port: parseInt(values.port || '0') || (typeLabel === 'SQL Server' ? 1433 : (typeLabel === 'PostgreSQL' ? 5432 : 3306)),
                username: values.username,
                password: values.password,
                id: dataSourceId // Pass existing ID for unmasking
            };

            const res = await apiPost('/datasources/discover-databases', token, payload);
            if (res.databases && Array.isArray(res.databases)) {
                setDiscoveredDbs(res.databases);
            }
        } catch (e: any) {
            setDiscoverError(e.message || "Failed to load databases. Check credentials.");
        } finally {
            setIsDiscovering(false);
        }
    };

    const buildConnectionString = (data: FormValues, type: string) => {
        if (type === '17') { // Salesforce
            let base = `EnvironmentUrl=${data.environmentUrl};ApiVersion=${data.apiVersion};`;

            if (sfAuthMode === 'oauth') {
                // OAuth Client Credentials
                return base + `ClientId=${data.clientId};ClientSecret=${data.clientSecret};GrantType=client_credentials;`;
            } else {
                // Legacy Username/Password
                let legacy = base + `GrantType=password;Username=${data.username};Password=${data.password};SecurityToken=${data.securityToken};`;
                if (data.clientId) legacy += `ClientId=${data.clientId};`;
                if (data.clientSecret) legacy += `ClientSecret=${data.clientSecret};`;
                return legacy;
            }
        }
        if (type === '15') return data.command;
        if (type === '16') return data.connectionString;
        return undefined; // Relational DBs use server-side construction or other logic
    };

    const onTest = async (data: FormValues) => {
        setIsTesting(true);
        setTestResult(null);
        try {
            if (selectedType === '15') { // MCP
                await apiPost('/mcp/connect', token, { command: data.command });
                setTestResult({ success: true, message: 'MCP Connection successful!' });
            } else if (selectedType === '17') { // Salesforce
                const connStr = buildConnectionString(data, '17');
                await apiPost('/datasources/test', token, {
                    ...data,
                    type: 17,
                    connectionString: connStr,
                    workspaceId: user?.tenantId // Use tenantId as workspaceId
                });
                setTestResult({ success: true, message: 'Salesforce Connection successful!' });
            } else {
                await apiPost('/datasources/test', token, {
                    ...data,
                    type: parseInt(selectedType!),
                    port: (data.port && data.port !== 'None') ? parseInt(data.port) : undefined,
                    id: dataSourceId, // Pass existing ID so backend can decrypt secrets if masked
                    workspaceId: user?.tenantId // Use tenantId as workspaceId
                });
                setTestResult({ success: true, message: 'Connection successful!' });
            }
        } catch (err: any) {
            setTestResult({ success: false, message: err.message || 'Connection failed' });
        } finally {
            setIsTesting(false);
        }
    };

    const onSubmit = async (data: FormValues) => {
        try {
            const payload = {
                ...data,
                ...data,
                type: parseInt(selectedType!),
                port: (data.port && data.port !== 'None') ? parseInt(data.port) : undefined,
                workspaceId: user?.tenantId, // Use tenantId as workspaceId
                connectionString: buildConnectionString(data, selectedType!),
                gatewayId: data.gatewayId === "none" ? null : data.gatewayId
            };

            if (dataSourceId) {
                // Edit
                await apiPut(`/datasources/${dataSourceId}`, token, payload);
            } else {
                await apiPost('/datasources', token, payload);
            }

            onSuccess();
            onClose();
        } catch (err: any) {
            setTestResult({ success: false, message: err.message || 'Failed to save connection.' });
        }
    };

    const onError = (errors: any) => {
        console.error("Form Validation Errors:", errors);
        setTestResult({ success: false, message: `Please fill in required fields: ${Object.keys(errors).join(', ')}` });
    };

    if (!isOpen) return null;

    const typeInfo = DATA_SOURCE_TYPES.find(t => t.value === selectedType);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-4xl h-[650px] bg-white dark:bg-[#0f172a] border border-slate-200 dark:border-white/10 rounded-3xl shadow-2xl flex overflow-hidden"
            >
                {/* Left Panel: Steps / Summary */}
                <div className="w-64 bg-slate-50 dark:bg-black/20 border-r border-slate-200 dark:border-white/5 p-6 flex flex-col justify-between">
                    <div>
                        <h2 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#002F5D] to-blue-500 mb-6">
                            {dataSourceId ? 'Edit Connection' : 'New Connection'}
                        </h2>
                        <div className="space-y-4">
                            <div className={`p-3 rounded-xl flex items-center gap-3 transition-colors ${step === 'GALLERY' ? 'bg-[#002F5D]/10 dark:bg-blue-500/20 text-[#002F5D] dark:text-blue-400' : 'text-slate-500 dark:text-gray-500'}`}>
                                <Database size={20} />
                                <span className="font-medium">Select Source</span>
                            </div>
                            <div className={`p-3 rounded-xl flex items-center gap-3 transition-colors ${step === 'CONFIG' ? 'bg-[#002F5D]/10 dark:bg-blue-500/20 text-[#002F5D] dark:text-blue-400' : 'text-slate-500 dark:text-gray-500'}`}>
                                <Server size={20} />
                                <span className="font-medium">Configure</span>
                            </div>
                        </div>
                    </div>
                    {selectedType && (
                        <div className="p-4 rounded-xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 shadow-sm">
                            <div className="text-xs text-slate-500 dark:text-gray-500 uppercase">Selected</div>
                            <div className="text-lg font-semibold text-slate-900 dark:text-white mt-1">{typeInfo?.label}</div>
                        </div>
                    )}
                </div>

                {/* Right Panel: Content */}
                <div className="flex-1 flex flex-col relative">
                    {/* Header */}
                    <div className="h-16 border-b border-slate-200 dark:border-white/5 flex items-center justify-between px-8 bg-white dark:bg-transparent">
                        {step === 'CONFIG' && (
                            <Button variant="ghost" className="text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10" onClick={() => setStep('GALLERY')}>
                                <ChevronLeft className="mr-2 h-4 w-4" /> Back to Gallery
                            </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={onClose} className="ml-auto text-slate-400 dark:text-gray-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10">
                            <X size={20} />
                        </Button>
                    </div>

                    {/* Content Area */}
                    <div className="flex-1 p-8 overflow-y-auto">
                        <AnimatePresence mode="wait">
                            {step === 'GALLERY' && (
                                <motion.div
                                    key="gallery"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    className="grid grid-cols-3 gap-4"
                                >
                                    {DATA_SOURCE_TYPES.map(t => (
                                        <ConnectorTile
                                            key={t.value}
                                            id={t.value}
                                            label={t.label}
                                            selected={false}
                                            icon={t.icon}
                                            onClick={() => handleSelectType(t.value)}
                                        />
                                    ))}
                                </motion.div>
                            )}

                            {step === 'CONFIG' && (
                                <motion.div
                                    key="config"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    className="max-w-xl mx-auto space-y-6"
                                >
                                    <form id="conn-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                                        <div className="space-y-2">
                                            <Label className="text-slate-700 dark:text-gray-300">Connection Name</Label>
                                            <Input placeholder="My Production DB" {...register('name')} className=" h-11 text-lg bg-white dark:bg-transparent border-slate-200 dark:border-white/20 text-slate-900 dark:text-white" autoFocus />
                                        </div>

                                        {/* ETL Outputs (Parquet type=12): Read-only info panel */}
                                        {selectedType === '12' && watch('source') === 'etl_workspace' && (
                                            <div className="rounded-xl border border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/10 p-5 space-y-3">
                                                <div className="flex items-start gap-3">
                                                    <div className="mt-0.5 w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center shrink-0">
                                                        <Database size={16} className="text-blue-600 dark:text-blue-400" />
                                                    </div>
                                                    <div>
                                                        <div className="font-semibold text-blue-800 dark:text-blue-300 text-sm">Auto-Managed ETL Workspace</div>
                                                        <p className="text-xs text-blue-600/80 dark:text-blue-400/80 mt-1">
                                                            This data source is automatically created and managed by your ETL flows.
                                                            Each time a flow with a <strong>Save as Data Model</strong> node runs, it writes
                                                            a Parquet file here that becomes available as a Data Model in the dashboard.
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="bg-white/60 dark:bg-black/20 rounded-lg p-3 space-y-1">
                                                    <div className="text-xs text-slate-500 dark:text-gray-500 uppercase tracking-wide font-medium">Storage Path</div>
                                                    <div className="text-xs font-mono text-slate-700 dark:text-gray-300 break-all">
                                                        {watch('connectionString') || 'App_Data/etl_outputs/…'}
                                                    </div>
                                                </div>
                                                <div className="text-xs text-blue-600/70 dark:text-blue-400/60">
                                                    ℹ️ No manual configuration is needed. Run a flow with a DATA_MODEL_OUTPUT node to populate this workspace.
                                                </div>
                                            </div>
                                        )}


                                        <div className="grid grid-cols-2 gap-4">
                                            {/* Common Fields */}
                                            {activeFields.includes('host') && (
                                                <div className="col-span-2 md:col-span-1 space-y-2">
                                                    <Label className="text-slate-700 dark:text-gray-300">Host</Label>
                                                    <Input placeholder="localhost" {...register('host')} className="bg-white dark:bg-transparent border-slate-200 dark:border-white/20 text-slate-900 dark:text-white" />
                                                </div>
                                            )}
                                            {activeFields.includes('port') && (
                                                <div className="col-span-2 md:col-span-1 space-y-2">
                                                    <Label className="text-slate-700 dark:text-gray-300">Port</Label>
                                                    <Input placeholder="5432" {...register('port')} className="bg-white dark:bg-transparent border-slate-200 dark:border-white/20 text-slate-900 dark:text-white" />
                                                </div>
                                            )}
                                            {activeFields.includes('username') && selectedType !== '17' && ( // Hide generic user for Salesforce, handled selectively
                                                <div className="col-span-2 md:col-span-1 space-y-2">
                                                    <Label className="text-slate-700 dark:text-gray-300">Username</Label>
                                                    <Input placeholder="user" {...register('username')} className="bg-white dark:bg-transparent border-slate-200 dark:border-white/20 text-slate-900 dark:text-white" />
                                                </div>
                                            )}
                                            {activeFields.includes('password') && selectedType !== '17' && ( // Hide generic pass for Salesforce
                                                <div className="col-span-2 md:col-span-1 space-y-2">
                                                    <Label className="text-slate-700 dark:text-gray-300">Password</Label>
                                                    <div className="relative">
                                                        <Input
                                                            type={showPassword ? "text" : "password"}
                                                            placeholder="********"
                                                            {...register('password')}
                                                            onFocus={(e) => {
                                                                if (e.target.value === '********') {
                                                                    setValue('password', '');
                                                                }
                                                            }}
                                                            onBlur={(e) => {
                                                                if (e.target.value === '') {
                                                                    setValue('password', '********');
                                                                }
                                                            }}
                                                            className="bg-white dark:bg-transparent border-slate-200 dark:border-white/20 text-slate-900 dark:text-white pr-10"
                                                        />
                                                        {watch('password') !== '********' && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setShowPassword(!showPassword)}
                                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                                                                tabIndex={-1}
                                                            >
                                                                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {activeFields.includes('database') && (
                                                <div className="col-span-2 space-y-2">
                                                    <Label className="text-slate-700 dark:text-gray-300">Database Name</Label>
                                                    <div className="flex gap-2">
                                                        <div className="flex-1">
                                                            {discoveredDbs.length > 0 ? (
                                                                <select
                                                                    className="flex h-11 w-full rounded-md border border-slate-200 dark:border-white/20 bg-white dark:bg-transparent px-3 py-2 text-sm ring-offset-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-800 dark:bg-slate-950 dark:ring-offset-slate-950 dark:placeholder:text-slate-400 dark:focus:ring-slate-300 dark:text-white"
                                                                    {...register('database')}
                                                                >
                                                                    <option value="">-- Select Database --</option>
                                                                    {discoveredDbs.map(db => <option key={db} value={db}>{db}</option>)}
                                                                </select>
                                                            ) : (
                                                                <Input placeholder="mydb" {...register('database')} className="bg-white dark:bg-transparent border-slate-200 dark:border-white/20 text-slate-900 dark:text-white" />
                                                            )}
                                                        </div>
                                                        {['0', '1', '2', '3'].includes(selectedType!) && (
                                                            <Button
                                                                type="button"
                                                                onClick={handleDiscoverDatabases}
                                                                disabled={isDiscovering}
                                                                variant="outline"
                                                                className="h-11 w-11 p-0 border-slate-200 dark:border-white/20"
                                                                title="Load Databases"
                                                            >
                                                                {isDiscovering ? <Loader2 className="animate-spin h-4 w-4" /> : <RefreshCw className="h-4 w-4" />}
                                                            </Button>
                                                        )}
                                                    </div>
                                                    {discoverError && (
                                                        <div className="flex items-center gap-2 text-xs text-red-500 dark:text-red-400 mt-1.5">
                                                            <AlertCircle size={12} />
                                                            <span>{discoverError}</span>
                                                        </div>
                                                    )}
                                                    {discoveredDbs.length > 0 && (
                                                        <div className="text-xs text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300 cursor-pointer mt-1 flex items-center gap-1" onClick={() => setDiscoveredDbs([])}>
                                                            <span>✕</span>
                                                            <span>Clear list — type name manually</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Snowflake Specific */}
                                            {activeFields.includes('account') && selectedType !== '17' && (
                                                <div className="col-span-2 space-y-2">
                                                    <Label className="text-slate-700 dark:text-gray-300">Account URL</Label>
                                                    <Input placeholder="xy12345.us-east-1" {...register('account')} className="bg-white dark:bg-transparent border-slate-200 dark:border-white/20 text-slate-900 dark:text-white" />
                                                </div>
                                            )}

                                            {/* Salesforce: Auth Selection */}
                                            {selectedType === '17' && (
                                                <div className="col-span-2 p-4 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 space-y-4">
                                                    <div className="flex items-center justify-between mb-4">
                                                        <Label className="text-slate-700 dark:text-gray-300 font-medium">Authentication Method</Label>
                                                        <div className="flex bg-slate-200 dark:bg-black/40 rounded-lg p-1">
                                                            <button
                                                                type="button"
                                                                onClick={() => setSfAuthMode('oauth')}
                                                                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${sfAuthMode === 'oauth' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white'}`}
                                                            >
                                                                OAuth 2.0 (Client Creds)
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => setSfAuthMode('legacy')}
                                                                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${sfAuthMode === 'legacy' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white'}`}
                                                            >
                                                                Legacy (User/Pass)
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <div className="space-y-4">
                                                        {/* Shared: Environment URL */}
                                                        <div className="space-y-2">
                                                            <Label className="text-slate-700 dark:text-gray-300">Environment URL</Label>
                                                            <Input placeholder="https://my-domain.my.salesforce.com" {...register('environmentUrl')} className="bg-white dark:bg-transparent border-slate-200 dark:border-white/20 text-slate-900 dark:text-white" />
                                                        </div>

                                                        {/* OAuth Mode */}
                                                        {sfAuthMode === 'oauth' && (
                                                            <>
                                                                <div className="space-y-2">
                                                                    <Label className="text-slate-700 dark:text-gray-300">Client ID (Consumer Key)</Label>
                                                                    <Input placeholder="3MVG9..." {...register('clientId')} className="bg-white dark:bg-transparent border-slate-200 dark:border-white/20 text-slate-900 dark:text-white" />
                                                                </div>
                                                                <div className="space-y-2">
                                                                    <Label className="text-slate-700 dark:text-gray-300">Client Secret</Label>
                                                                    <Input type="password" placeholder="Warning: Keep secret" {...register('clientSecret')} className="bg-white dark:bg-transparent border-slate-200 dark:border-white/20 text-slate-900 dark:text-white" />
                                                                </div>
                                                            </>
                                                        )}

                                                        {/* Legacy Mode */}
                                                        {sfAuthMode === 'legacy' && (
                                                            <>
                                                                <div className="grid grid-cols-2 gap-4">
                                                                    <div className="space-y-2">
                                                                        <Label className="text-slate-700 dark:text-gray-300">Username</Label>
                                                                        <Input placeholder="user@company.com" {...register('username')} className="bg-white dark:bg-transparent border-slate-200 dark:border-white/20 text-slate-900 dark:text-white" />
                                                                    </div>
                                                                    <div className="space-y-2">
                                                                        <Label className="text-slate-700 dark:text-gray-300">Password</Label>
                                                                        <Input type="password" placeholder="Password" {...register('password')} className="bg-white dark:bg-transparent border-slate-200 dark:border-white/20 text-slate-900 dark:text-white" />
                                                                    </div>
                                                                </div>
                                                                <div className="space-y-2">
                                                                    <Label className="text-slate-700 dark:text-gray-300">Security Token</Label>
                                                                    <Input type="password" placeholder="Verification Token from Salesforce" {...register('securityToken')} className="bg-white dark:bg-transparent border-slate-200 dark:border-white/20 text-slate-900 dark:text-white" />
                                                                </div>
                                                                {/* Optional Client ID/Secret for Legacy */}
                                                                <div className="pt-2">
                                                                    <div className="text-xs text-slate-500 dark:text-gray-500 mb-2 uppercase tracking-wide font-medium">Optional Connected App Details</div>
                                                                    <div className="grid grid-cols-2 gap-4">
                                                                        <div className="space-y-2">
                                                                            <Label className="text-slate-500 dark:text-gray-400 text-xs">Client ID</Label>
                                                                            <Input placeholder="Optional" {...register('clientId')} className="bg-white dark:bg-transparent border-slate-200 dark:border-white/20 text-slate-900 dark:text-white h-9 text-sm" />
                                                                        </div>
                                                                        <div className="space-y-2">
                                                                            <Label className="text-slate-500 dark:text-gray-400 text-xs">Client Secret</Label>
                                                                            <Input type="password" placeholder="Optional" {...register('clientSecret')} className="bg-white dark:bg-transparent border-slate-200 dark:border-white/20 text-slate-900 dark:text-white h-9 text-sm" />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </>
                                                        )}

                                                        {/* Shared: API Version */}
                                                        <div className="space-y-2 pt-2">
                                                            <Label className="text-slate-700 dark:text-gray-300">API Version</Label>
                                                            <Input placeholder="52.0" {...register('apiVersion')} className="bg-white dark:bg-transparent border-slate-200 dark:border-white/20 text-slate-900 dark:text-white" />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Generic Form Fields (Only render if NOT a file source) */}
                                            {!isFileSource && activeFields.includes('filePath') && (
                                                <div className="col-span-2 space-y-2">
                                                    <Label className="text-slate-700 dark:text-gray-300">File Path or URL</Label>
                                                    <Input placeholder="https://..." {...register('filePath')} className="bg-white dark:bg-transparent border-slate-200 dark:border-white/20 text-slate-900 dark:text-white" />
                                                </div>
                                            )}
                                            {activeFields.includes('command') && (
                                                <div className="col-span-2 space-y-2">
                                                    <Label className="text-slate-700 dark:text-gray-300">Command</Label>
                                                    <Input placeholder="npx -y..." {...register('command')} className="bg-white dark:bg-transparent border-slate-200 dark:border-white/20 text-slate-900 dark:text-white" />
                                                </div>
                                            )}
                                            {activeFields.includes('connectionString') && (
                                                <div className="col-span-2 space-y-2">
                                                    <Label className="text-slate-700 dark:text-gray-300">Connection String</Label>
                                                    <Input placeholder="DefaultEndpointsProtocol=https;AccountName=...;" {...register('connectionString')} className="bg-white dark:bg-transparent border-slate-200 dark:border-white/20 text-slate-900 dark:text-white" />
                                                </div>
                                            )}
                                        </div>

                                        {/* Dropzone UI for Files */}
                                        {isFileSource && (
                                            <div className="col-span-2 pt-2">
                                                <div
                                                    className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                                                        dragActive
                                                            ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/20'
                                                            : 'border-slate-300 dark:border-slate-700 hover:border-blue-400 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                                                    }`}
                                                    onDragEnter={handleDrag}
                                                    onDragLeave={handleDrag}
                                                    onDragOver={handleDrag}
                                                    onDrop={handleDrop}
                                                >
                                                    <input
                                                        ref={fileInputRef}
                                                        type="file"
                                                        className="hidden"
                                                        accept=".csv,.xlsx,.xls,.parquet,.json"
                                                        onChange={(e) => {
                                                            if (e.target.files && e.target.files[0]) handleFileSelection(e.target.files[0]);
                                                        }}
                                                    />

                                                    {selectedFile ? (
                                                        /* File selected — show it */
                                                        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="space-y-4">
                                                            <div className="w-16 h-16 mx-auto bg-green-100 dark:bg-green-900/30 text-green-600 rounded-full flex items-center justify-center shadow-sm">
                                                                <FileSpreadsheet size={28} />
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-semibold text-slate-900 dark:text-white truncate max-w-xs mx-auto">{selectedFile.name}</p>
                                                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                                                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB{dataSourceId ? ' · Replacing existing file' : ''}
                                                                </p>
                                                            </div>
                                                            <Button type="button" variant="outline" size="sm" onClick={() => setSelectedFile(null)} disabled={isUploading} className="mt-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20">
                                                                Remove File
                                                            </Button>
                                                        </motion.div>
                                                    ) : existingFileName && dataSourceId ? (
                                                        /* Editing — show existing stored file */
                                                        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="space-y-3">
                                                            <div className="w-16 h-16 mx-auto bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-full flex items-center justify-center shadow-sm">
                                                                <FileSpreadsheet size={28} />
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-semibold text-slate-900 dark:text-white truncate max-w-xs mx-auto">{existingFileName}</p>
                                                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Currently stored in cloud · <span className="text-blue-500 cursor-pointer hover:underline" onClick={() => fileInputRef.current?.click()}>Replace file</span></p>
                                                            </div>
                                                        </motion.div>
                                                    ) : (
                                                        /* New datasource — empty drop zone */
                                                        <div className="space-y-4 cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                                                            <div className="w-16 h-16 mx-auto bg-blue-50 dark:bg-slate-800 text-blue-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                                                                <UploadCloud size={32} />
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                                                    <span className="text-blue-600 dark:text-blue-400 hover:underline">Click to upload</span> or drag and drop
                                                                </p>
                                                                <p className="text-xs text-slate-500 dark:text-slate-500 mt-2">
                                                                    CSV, Excel (.xlsx/.xls), JSON, or Parquet files
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Progress Bar */}
                                                {isUploading && (
                                                    <div className="mt-4 space-y-2">
                                                        <div className="flex justify-between text-xs font-medium text-slate-600 dark:text-slate-400">
                                                            <span>Uploading & Profiling Data...</span>
                                                            <span>{uploadProgress}%</span>
                                                        </div>
                                                        <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
                                                            <motion.div
                                                                className="bg-blue-600 h-2 rounded-full"
                                                                initial={{ width: 0 }}
                                                                animate={{ width: `${uploadProgress}%` }}
                                                                transition={{ ease: "easeOut", duration: 0.5 }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Gateway Selection */}
                                        {!isFileSource && ['0', '1', '2', '3'].includes(selectedType!) && (
                                            <div className="p-4 rounded-xl bg-gradient-to-r from-blue-500/5 to-transparent border border-blue-500/20">
                                                <Label className="text-blue-600 dark:text-blue-200">Connect via On-Premise Gateway?</Label>
                                                <p className="text-xs text-slate-500 dark:text-gray-400 mb-3">Required for accessing local databases behind firewalls.</p>
                                                <select
                                                    className="w-full h-10 bg-white dark:bg-black/40 border border-slate-200 dark:border-white/10 rounded-lg px-3 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                                    {...register('gatewayId')}
                                                >
                                                    <option value="none">Direct Connection (Cloud to Cloud)</option>
                                                    {gateways.map(g => (
                                                        <option key={g.id} value={g.id}>
                                                            {g.name} ({g.isOnline ? 'Online' : 'Offline'})
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        {/* Status Area */}
                                        {testResult && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                className={`p-4 rounded-xl flex items-center gap-3 ${testResult.success ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}
                                            >
                                                {testResult.success ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
                                                <span className="text-sm font-medium">{testResult.message}</span>
                                            </motion.div>
                                        )}

                                        <div className="flex gap-4 pt-4">
                                            {/* Normal Database Actions */}
                                            {!isFileSource && (
                                                <>
                                                    <Button type="button" onClick={handleSubmit(onTest, onError)} disabled={isTesting} className="flex-1 bg-[#002F5D] hover:bg-[#004275] text-white shadow-lg shadow-blue-900/10 transition-all hover:scale-[1.02]">
                                                        {isTesting ? <Loader2 className="animate-spin mr-2" /> : <Play className="mr-2 h-4 w-4" />}
                                                        Test Connection
                                                    </Button>
                                                    <Button type="button" onClick={handleSubmit(onSubmit, onError)} className="flex-[2] bg-[#002F5D] hover:bg-[#004275] text-white shadow-lg shadow-blue-900/10 transition-all hover:scale-[1.02]">
                                                        <Save className="mr-2 h-4 w-4" />
                                                        {dataSourceId ? 'Update Connection' : 'Create Connection'}
                                                    </Button>
                                                </>
                                            )}

                                            {/* File Upload Action */}
                                            {isFileSource && (
                                                <Button
                                                    type="button"
                                                    onClick={selectedFile ? handleFileUpload : async () => {
                                                        // Name-only update — no new file selected
                                                        try {
                                                            await apiPut(`/datasources/${dataSourceId}`, token, { name: watch('name') });
                                                            onSuccess();
                                                            onClose();
                                                        } catch (e: any) {
                                                            setTestResult({ success: false, message: e.message || 'Failed to save.' });
                                                        }
                                                    }}
                                                    disabled={isUploading}
                                                    className="w-full bg-[#002F5D] hover:bg-[#004275] text-white shadow-lg transition-all hover:scale-[1.02]"
                                                >
                                                    {isUploading ? (
                                                        <><Loader2 className="animate-spin mr-2 h-4 w-4" /> Processing File...</>
                                                    ) : selectedFile ? (
                                                        <><UploadCloud className="mr-2 h-4 w-4" /> Upload & Replace Source</>
                                                    ) : (
                                                        <><Save className="mr-2 h-4 w-4" /> {dataSourceId ? 'Save Changes' : 'Upload & Create Source'}</>
                                                    )}
                                                </Button>
                                            )}
                                        </div>

                                    </form>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </motion.div >
        </div >
    );
};
